import { useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';

import { ROUTES } from '#/config/routes';
import { createSupplier } from '#/api/srm/supplier/create';
import { fetchSupplierDetail } from '#/api/srm/supplier/fetchDetail';
import { updateSupplier } from '#/api/srm/supplier/update';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import { Button } from '#/modules/commonComponents/button';
import PageShell from '#/modules/infra/layout/PageShell';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import SupplierAddressesEditor from '#/modules/srmMgmt/supplier/components/SupplierAddressesEditor';
import SupplierContactsEditor from '#/modules/srmMgmt/supplier/components/SupplierContactsEditor';
import {
  getSupplierApiErrorMessage,
  isSupplierVersionConflict,
} from '#/modules/srmMgmt/supplier/helper/apiError';
import {
  buildSupplierWritePayload,
  mapSupplierDetailToForm,
} from '#/modules/srmMgmt/supplier/helper/buildSupplierWritePayload';
import {
  SUPPLIER_FIELD_LABELS,
  resolveSupplierFieldLabel,
} from '#/modules/srmMgmt/supplier/helper/formValidationLabels';
import {
  SUPPLIER_FORM_DEFAULT_VALUES,
  supplierFormSchema,
} from '#/modules/srmMgmt/supplier/schemas/supplierSchema';
import {
  supplierDetailQueryKey,
  supplierListQueryKeyPrefix,
} from '#/utils/queryKeys';

const CreateEditSupplierPage = () => {
  const { id } = useParams();
  const updatedAtRef = useRef(null);
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: supplierDetailQueryKey(id),
    queryFn: () => fetchSupplierDetail(id),
    enabled: Boolean(id),
  });

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    createFn: createSupplier,
    updateFn: updateSupplier,
    buildPayload: (values) => buildSupplierWritePayload(values, {
      updatedAt: id ? updatedAtRef.current : null,
    }),
    listQueryKeyPrefix: supplierListQueryKeyPrefix,
    detailQueryKey: supplierDetailQueryKey,
    getEditPath: ROUTES.supplierEdit,
    getErrorMessage: (error) => getSupplierApiErrorMessage(error, '儲存失敗'),
    isVersionConflict: isSupplierVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(supplierFormSchema),
    defaultValues: SUPPLIER_FORM_DEFAULT_VALUES,
  });

  useEffect(() => {
    const detail = detailQuery.data?.data;
    if (!detail) {
      return;
    }

    updatedAtRef.current = detail.updatedAt ?? detail.record?.updatedAt ?? null;
    resetValidationSummary();
    reset(mapSupplierDetailToForm(detail));
  }, [detailQuery.data, reset, resetValidationSummary]);

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    save(values);
  }

  const pageTitle = isEdit ? '編輯供應商' : '新增供應商';
  const isPageLoading = isEdit && detailQuery.isLoading;
  const isDetailNotFound = isEdit && detailQuery.isError;

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-6xl">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.supplier}>返回列表</Link>
            </Button>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-muted-foreground">載入中…</p>
          ) : isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getSupplierApiErrorMessage(detailQuery.error, '找不到供應商資料')}
            </p>
          ) : (
            <>
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={SUPPLIER_FIELD_LABELS}
                fieldLabelResolver={resolveSupplierFieldLabel}
                customMessages={customMessages}
              />
              <form
                className="sheet create_edit create_edit_form create_edit_form--page-scroll"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body divide-y divide-gray-500">
                  <section className="pb-6">
                    <div className="form-block">
                      <h2 className="form-block__title">基本資料</h2>
                    </div>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <FloatingInput
                        id="code"
                        label="代號"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="name"
                        label="名稱"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="chineseName"
                        label="中文名"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="englishName"
                        label="英文名"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="taxIdNumber"
                        label="統編"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="homepage"
                        label="網址"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="phone"
                        label="電話"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="fax"
                        label="傳真"
                        register={register}
                        errors={errors}
                      />
                    </div>
                    <FloatingTextarea
                      id="remark"
                      label="備註"
                      register={register}
                      errors={errors}
                    />
                  </section>

                  <SupplierAddressesEditor
                    control={control}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                  />

                  <SupplierContactsEditor
                    control={control}
                    register={register}
                    errors={errors}
                    setValue={setValue}
                  />
                </div>

                <div className="create_edit_form__actions flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.supplier}>取消</Link>
                  </Button>
                  <Button type="submit" disabled={isPending}>
                    {isPending ? '儲存中…' : '儲存'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditSupplierPage;
